import { isNativePlatform } from './platform';

/**
 * Keeps a `position: fixed; top: 0` layer (the status-bar scrim, the
 * connection banner) at the top of what the player can actually see.
 *
 * iOS WebKit anchors fixed elements to the layout viewport. When the keyboard
 * opens over a page that cannot scroll far enough to reveal the focused field
 * (the sign-in screen), WebKit pans the visual viewport below the layout
 * viewport instead: the whole page moves up, the logo slides under the clock
 * and Dynamic Island, and every fixed top layer leaves the screen with the
 * layout viewport. `visualViewport.offsetTop` is exactly that pan, so moving
 * the layer down by it puts it back under the status bar.
 */

type OffsetSource = Pick<VisualViewport, 'offsetTop'>;
type ViewportEvents = OffsetSource & Pick<VisualViewport, 'addEventListener' | 'removeEventListener'>;
type ScrollEvents = Pick<Window, 'addEventListener' | 'removeEventListener'>;

/** How far, in CSS pixels, the visible area sits below the layout viewport's top (0 when it does not). */
export function visualViewportOffset(viewport: OffsetSource | null | undefined): number {
  const offset = viewport?.offsetTop ?? 0;
  // Negative values come from rubber-band overscroll at the top; the layer
  // stays where CSS put it then.
  return Number.isFinite(offset) && offset > 0 ? offset : 0;
}

/** The transform that moves a top-anchored fixed layer onto the visual viewport ('' when none is needed). */
export function pinTransform(offset: number): string {
  return offset > 0 ? `translate3d(0, ${offset}px, 0)` : '';
}

/**
 * Follows the visual viewport with `element` until the returned cleanup runs.
 * Listens to the viewport's own scroll and resize (keyboard opening, closing,
 * panning) and to document scroll, which can move the layout viewport under
 * an already panned visual viewport.
 */
export function pinToVisualViewport(
  element: HTMLElement,
  viewport: ViewportEvents | null | undefined = typeof window === 'undefined' ? undefined : window.visualViewport,
  scroller: ScrollEvents | null = typeof window === 'undefined' ? null : window,
): () => void {
  if (!viewport) return () => {};
  const update = () => { element.style.transform = pinTransform(visualViewportOffset(viewport)); };
  viewport.addEventListener('scroll', update, { passive: true });
  viewport.addEventListener('resize', update, { passive: true });
  scroller?.addEventListener('scroll', update, { passive: true });
  update();
  return () => {
    viewport.removeEventListener('scroll', update);
    viewport.removeEventListener('resize', update);
    scroller?.removeEventListener('scroll', update);
    element.style.transform = '';
  };
}

/**
 * Ref callback for a fixed top layer: pinned to the visual viewport in the
 * store app only. Browsers (desktop and mobile web) keep the plain CSS
 * position and get no listeners. Stable identity, so React attaches it once;
 * React 19 runs the returned cleanup when the element unmounts.
 */
export function pinNativeTopLayer(element: HTMLElement | null): (() => void) | undefined {
  if (!element || !isNativePlatform()) return undefined;
  return pinToVisualViewport(element);
}
